import React, { useEffect, useState } from 'react';
import { Col, Container, Row, Form, Card, Spinner, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import MetaTag from '@components/MetaTag';
import Footer from '@components/Footer';
import HeaderPrimary from '@components/HeaderPrimary';
import { Pagination } from '@components/Base';
import { useHospitalNetworkPage } from './hook';
import { ProvinceApi } from '@apis/ProvinceApi';
import { HealthPlanApi } from '@apis/HealthPlanApi';
import { Hospital } from '@models/BusinessPartner';
import { HealthPlan } from '@models/HealthPlan';
import { FilterProvider, useFilterContext } from './FilterProvider';

const HospitalNetworkPageContent = () => {
  const { t } = useTranslation();
  const { hospitals, isLoading, error, pagination, fetchHospitals } = useHospitalNetworkPage();
  const { filters, updateFilters } = useFilterContext();
  const [provinces, setProvinces] = useState<string[]>([]);
  const [healthPlans, setHealthPlans] = useState<HealthPlan[]>([]);

  useEffect(() => {
    ProvinceApi.getAll()
      .then(setProvinces)
      .catch(() => setProvinces([]));

    HealthPlanApi.getPublic()
      .then(setHealthPlans)
      .catch(() => setHealthPlans([]));
  }, []);

  useEffect(() => {
    fetchHospitals({
      search: filters.search || undefined,
      province: filters.province || undefined,
      healthPlanId: filters.healthPlanId || undefined,
      page: filters.page,
    });
  }, [filters]);

  return (
    <>
      <MetaTag pageName="hospitalNetworkPage" />
      <HeaderPrimary />
      <main>
        <section className="bg-primary text-white py-5">
          <Container>
            <Row className="mb-4">
              <Col>
                <h1 className="text-center mb-3">{t('hospitalNetworkPage.heading')}</h1>
                <p className="text-center lead">{t('hospitalNetworkPage.subHeading')}</p>
                <p className="text-center">{t('hospitalNetworkPage.description')}</p>
              </Col>
            </Row>
          </Container>
        </section>
        <section className="bg-light py-5">
          <Container>
            <Row className="mb-4">
              <Col md={4}>
                <Form.Select
                  value={filters.healthPlanId}
                  onChange={(e) => updateFilters({ healthPlanId: e.target.value })}
                >
                  <option value="">{t('hospitalNetworkPage.allHealthPlans')}</option>
                  {healthPlans.map((healthPlan) => (
                    <option key={healthPlan.id} value={healthPlan.id}>
                      {healthPlan.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={4} className="mb-3 mb-md-0">
                <Form.Select value={filters.province} onChange={(e) => updateFilters({ province: e.target.value })}>
                  <option value="">{t('hospitalNetworkPage.allLocations')}</option>
                  {provinces.map((province) => (
                    <option key={province} value={province}>
                      {province}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={4} className="mb-3 mb-md-0">
                <Form.Control
                  type="text"
                  placeholder={t('hospitalNetworkPage.searchPlaceholder')}
                  value={filters.search}
                  onChange={(e) => updateFilters({ search: e.target.value })}
                />
              </Col>
            </Row>

            {pagination && (
              <Row className="mb-3">
                <Col>
                  <p className="text-muted">
                    {t('hospitalNetworkPage.showingResults', {
                      from: (pagination.page - 1) * pagination.perPage + 1,
                      to: Math.min(pagination.page * pagination.perPage, pagination.total),
                      total: pagination.total,
                    })}
                  </p>
                </Col>
              </Row>
            )}

            {error ? (
              <Row>
                <Col className="text-center py-5">
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                </Col>
              </Row>
            ) : isLoading ? (
              <Row>
                <Col className="text-center py-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">{t('common:loading')}</span>
                  </Spinner>
                </Col>
              </Row>
            ) : !hospitals || hospitals.length === 0 ? (
              <Row>
                <Col className="text-center py-5">
                  <p className="text-muted">{t('hospitalNetworkPage.noHospitalsFound')}</p>
                </Col>
              </Row>
            ) : (
              <>
                <Row>
                  {hospitals.map((hospital: Hospital) => (
                    <Col key={hospital.id} md={6} lg={4} className="mb-4">
                      <Card className="h-100 shadow-sm hover-shadow transition">
                        <Card.Body className="d-flex flex-column">
                          <Card.Title className="mb-3">
                            <i className="bi bi-hospital me-2 text-primary"></i>
                            {hospital.name}
                          </Card.Title>
                          <Card.Text className="flex-grow-1">
                            <div className="mb-3">
                              <div className="mb-2">
                                <i className="bi bi-geo-alt-fill text-muted me-2"></i>
                                <strong className="text-muted">{t('hospitalNetworkPage.address')}:</strong>
                              </div>
                              <div className="ms-4 text-secondary">{hospital.fullAddress}</div>
                            </div>
                            <div className="mb-3">
                              <i className="bi bi-pin-map-fill text-muted me-2"></i>
                              <Badge bg="secondary" className="me-2">
                                {hospital.provinceName}
                              </Badge>
                            </div>
                            {hospital.telNumber && (
                              <div className="mb-3">
                                <i className="bi bi-telephone-fill text-muted me-2"></i>
                                <a href={`tel:${hospital.telNumber}`} className="text-decoration-none">
                                  {hospital.telNumber}
                                </a>
                              </div>
                            )}
                          </Card.Text>
                          <div className="mt-auto pt-3 border-top">
                            {hospital.googleMapUrl ? (
                              <a
                                href={hospital.googleMapUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-outline-primary btn-sm w-100"
                              >
                                <i className="bi bi-map me-2"></i>
                                {t('hospitalNetworkPage.viewOnMap')}
                              </a>
                            ) : (
                              <div className="text-muted small text-center">
                                {t('hospitalNetworkPage.mapNotAvailable')}
                              </div>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
                {pagination && pagination.totalPages > 1 && (
                  <Row className="mt-4">
                    <Col className="d-flex justify-content-center">
                      <Pagination page={pagination.page} totalPages={pagination.totalPages} />
                    </Col>
                  </Row>
                )}
              </>
            )}
          </Container>
        </section>
      </main>
      <Footer />
    </>
  );
};

const HospitalNetworkPage = () => {
  return (
    <FilterProvider>
      <HospitalNetworkPageContent />
    </FilterProvider>
  );
};

export { HospitalNetworkPage };
